import type {
  CheckResult,
  KubeCronJobsLookup,
  KubeEventsLookup,
  KubeWorkload,
  KubeWorkloadsLookup,
} from "@central-hub/contracts";
import type { Session } from "./auth";
import { Certificates, Cluster } from "./Dashboard";
import { ago, dayClock, plural } from "./format";
import { useLookup } from "./hooks";
import { Icon } from "./icons";
import { detailHref } from "./links";
import { STATUS_LABEL, type ViewModel, volumeRows } from "./model";

/** Live parts refresh at this pace: the wall's checks already poll every 15 s. */
const LIVE_MS = 30_000;

/**
 * The cluster view: nodes, storage and certificates from the hub's checks, every volume of
 * every site, and, read live, the workloads of each namespace, the CronJobs and the latest
 * warning events. For callers who see everything, or, of their own sites only, for those given
 * the cluster views (the hub narrows every answer).
 */
export function ClusterPage({
  vm,
  checks,
  session,
  now,
  stale,
}: {
  vm: ViewModel;
  checks: readonly CheckResult[];
  session: Session;
  now: number;
  stale: boolean;
}) {
  const workloads = useLookup<KubeWorkloadsLookup>(session, "kubernetes", "workloads", {}, LIVE_MS);
  const cronJobs = useLookup<KubeCronJobsLookup>(session, "kubernetes", "cronjobs", {}, LIVE_MS);
  const events = useLookup<KubeEventsLookup>(session, "kubernetes", "cluster-events", {}, LIVE_MS);
  const volumes = volumeRows(checks);

  return (
    <main className="view cluster-page" data-stale={stale}>
      <div className="cluster-top">
        <Cluster cluster={vm.cluster} />
        <Certificates certs={vm.certs} />
      </div>

      {volumes.length > 0 && (
        <section className="panel" aria-labelledby="volumes-title">
          <div className="panel-head">
            <h2 id="volumes-title" className="eyebrow">
              Volumes
            </h2>
            <span className="num">
              {plural(volumes.length, "volume", "volumes")}, fullest first
            </span>
          </div>
          <ul className="volumes">
            {volumes.map((v) => (
              <li key={`${v.target}/${v.pvc}`} className="volume" data-status={v.status}>
                <Icon name={v.status} label={STATUS_LABEL[v.status]} />
                <a href={detailHref(v.target)}>{v.target}</a>
                <span className="mono ellipsis" title={v.pvc}>
                  {v.pvc}
                </span>
                <span className="volume-bar" aria-hidden="true">
                  <span style={{ width: `${Math.round((v.ratio ?? 0) * 100)}%` }} />
                </span>
                <span className="num volume-text">{v.message}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="panel" aria-labelledby="workloads-title">
        <div className="panel-head">
          <h2 id="workloads-title" className="eyebrow">
            Workloads
          </h2>
          <span className="num">live</span>
        </div>
        <LookupState error={workloads.error} loading={!workloads.data} />
        {workloads.data && <Workloads workloads={workloads.data.workloads} />}
      </section>

      <section className="panel" aria-labelledby="cronjobs-title">
        <div className="panel-head">
          <h2 id="cronjobs-title" className="eyebrow">
            CronJobs
          </h2>
          <span className="num">live</span>
        </div>
        <LookupState error={cronJobs.error} loading={!cronJobs.data} />
        {cronJobs.data && (
          <div className="table-scroll">
            <table className="deploy-table">
              <thead>
                <tr>
                  <th scope="col">CronJob</th>
                  <th scope="col">Schedule (UTC)</th>
                  <th scope="col">Last job</th>
                  <th scope="col">Last success</th>
                </tr>
              </thead>
              <tbody>
                {cronJobs.data.cronJobs.map((c) => {
                  const status =
                    c.lastJob?.status === "failed"
                      ? "down"
                      : c.lastJob?.status === "succeeded"
                        ? "up"
                        : "unknown";
                  return (
                    <tr key={`${c.namespace}/${c.name}`}>
                      <th scope="row">
                        <span className="muted">{c.namespace}/</span>
                        {c.name}
                        {c.suspended && <span className="tag">suspended</span>}
                      </th>
                      <td className="mono">{c.schedule}</td>
                      <td>
                        {c.active > 0 ? (
                          <span className="deploy-now">
                            <span className="deploy-pulse" aria-hidden="true" />
                            running
                          </span>
                        ) : c.lastJob ? (
                          <span className="deploy-last" data-status={status}>
                            <Icon name={status} label={c.lastJob.status} />
                            {c.lastJob.status}
                            {c.lastJob.finishedAt &&
                              ` · ${ago(now - Date.parse(c.lastJob.finishedAt))}`}
                          </span>
                        ) : (
                          <span className="muted">never ran</span>
                        )}
                      </td>
                      <td className="num">
                        {c.lastSuccess ? dayClock(c.lastSuccess) : <span className="muted">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="panel" aria-labelledby="events-title">
        <div className="panel-head">
          <h2 id="events-title" className="eyebrow">
            Warning events
          </h2>
          <span className="num">live · Kubernetes keeps them about an hour</span>
        </div>
        <LookupState error={events.error} loading={!events.data} />
        {events.data && <EventList events={events.data.events} now={now} />}
      </section>
    </main>
  );
}

export function LookupState({ error, loading }: { error: string | null; loading: boolean }) {
  if (error) {
    return (
      <p className="notice" data-status="down" role="alert">
        <Icon name="down" />
        <span>Couldn’t read it: {error}</span>
      </p>
    );
  }
  return loading ? <p className="muted">Loading…</p> : null;
}

export function EventList({ events, now }: { events: KubeEventsLookup["events"]; now: number }) {
  if (events.length === 0) return <p className="muted">No events.</p>;
  return (
    <ul className="events">
      {events.map((e) => (
        <li
          key={`${e.namespace}/${e.object}/${e.reason}/${e.lastSeen}`}
          className="event"
          data-status={e.type === "Warning" ? "degraded" : "up"}
        >
          <span className="num event-when">
            {e.lastSeen ? ago(now - Date.parse(e.lastSeen)) : "—"}
          </span>
          <span className="event-what">
            <b>{e.reason}</b>
            {e.count > 1 && <span className="muted"> ×{e.count}</span>}{" "}
            <span className="mono muted">
              {e.namespace && `${e.namespace}/`}
              {e.object}
            </span>
          </span>
          <span className="event-message">{e.message}</span>
        </li>
      ))}
    </ul>
  );
}

function Workloads({ workloads }: { workloads: readonly KubeWorkload[] }) {
  const namespaces = [...new Set(workloads.map((w) => w.namespace))];
  return (
    <div className="workloads">
      {namespaces.map((ns) => {
        const own = workloads.filter((w) => w.namespace === ns);
        const short = own.filter((w) => w.ready < w.desired);
        return (
          <details key={ns} className="workload-ns" open={short.length > 0}>
            <summary>
              <Icon name={short.length > 0 ? "degraded" : "up"} />
              <b>{ns}</b>
              <span className="muted">
                {plural(own.length, "workload", "workloads")}
                {short.length > 0 && ` · ${short.length} not ready`}
              </span>
            </summary>
            <ul>
              {own.map((w) => (
                <li
                  key={`${w.kind}/${w.name}`}
                  className="workload"
                  data-status={w.ready < w.desired ? "degraded" : "up"}
                >
                  <span className="num workload-ready">
                    {w.ready}/{w.desired}
                  </span>
                  <span className="mono ellipsis">{w.name}</span>
                  <span className="muted">{w.kind}</span>
                  <span className="mono muted ellipsis" title={w.images.join(", ")}>
                    {w.images.map((i) => i.split("/").pop()).join(", ")}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        );
      })}
    </div>
  );
}
